<template>
  <div class="adviser-benefit-card">
    <div class="card-header">
      <div class="icon-circle">
        <img :src="iconSrc" alt="" width="24" height="24" />
      </div>
      <h2 class="card-title">{{ title }}</h2>
    </div>
    <p class="card-description">{{ description }}</p>
    <button type="button" class="card-action" @click="activate">
      {{ buttonText }}
    </button>
  </div>
</template>

<script>
  // Spec 4.3 "card_benefit" (icon header, title, description, outline pill
  // action). Exactly one of route / href / download should be given.
  // Figma card_benefit icons (24px, #306E2B). Which card gets which glyph
  // differs per page, see constants/adviser-enrolment/adviser-content.js.
  const ICONS = {
    // Arrows around a question mark: FAQs card on /ifa, /pf, /adviser/resources.
    'question-exchange': require('@/assets/shared/redesign/icons/adviser/question-exchange.svg'),
    // Two people with a speech bubble: FAQs card on /adviser only.
    conversation: require('@/assets/shared/redesign/icons/adviser/conversation.svg'),
    // Checklist: Resources / Additional Resources cards on every page.
    checklist: require('@/assets/shared/redesign/icons/adviser/checklist.svg'),
    document: require('@/assets/shared/redesign/icons/adviser/document.svg'),
    calculate: require('@/assets/shared/redesign/icons/adviser/calculate.svg'),
  };

  export default {
    name: 'AdviserBenefitCard',
    props: {
      icon: {
        type: String,
        required: true,
        validator: value => Object.keys(ICONS).includes(value),
      },
      title: {
        type: String,
        required: true,
      },
      description: {
        type: String,
        required: true,
      },
      buttonText: {
        type: String,
        required: true,
      },
      // vue-router route name.
      route: {
        type: String,
        default: '',
      },
      // Absolute or site-relative URL opened in the same tab.
      href: {
        type: String,
        default: '',
      },
      // Bare filename inside public/pdfs/, downloaded with the same name.
      download: {
        type: String,
        default: '',
      },
    },
    computed: {
      iconSrc() {
        return ICONS[this.icon];
      },
    },
    methods: {
      activate() {
        if (this.download) {
          // Same approach as downloadCustomerBrochure() in
          // AdviserEnrolmentContent.vue: an anchor with the download attribute.
          // The path must be absolute: on nested routes such as
          // /adviser/resources a relative "pdfs/…" resolves to /adviser/pdfs/…,
          // which the SPA fallback answers with index.html.
          const link = document.createElement('a');
          link.href = `${process.env.BASE_URL}pdfs/${this.download}`;
          link.download = this.download;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
        if (this.route) {
          this.$router.push({ name: this.route });
          return;
        }
        if (this.href) {
          window.location.assign(this.href);
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  @import '../../../constants/styles/adviser-pages.scss';

  .adviser-benefit-card {
    @include adviser-card;
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 5px 10px rgba(13, 13, 13, 0.15);
    }

    .card-header {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    .icon-circle {
      width: 48px;
      height: 48px;
      border-radius: 30px;
      background: $adviser-green-icon-bg;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .card-title {
      @include adviser-font;
      font-weight: 600;
      font-size: 24px;
      line-height: 30px;
      color: $adviser-text-black;
      margin: 0;
    }
    .card-description {
      @include adviser-body;
      color: $adviser-text-black;
      margin: 0;
      flex: 1;
    }
    .card-action {
      @include adviser-font;
      align-self: flex-start;
      height: 28px;
      padding: 0 12px;
      border-radius: 96px;
      border: 1px solid $adviser-green-dark;
      background: transparent;
      color: $adviser-green-dark;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      cursor: pointer;

      &:hover {
        background: $adviser-gradient-button;
        border-color: transparent;
        color: #ffffff;
      }
      &:focus-visible {
        outline: 2px solid $adviser-focus;
        outline-offset: 2px;
      }
    }
  }
</style>
